import React from 'react';
import styled from 'styled-components';

import Modal from '../common/Modal';
import { DialogHeader, DialogText, DialogTitle } from './dialogStyles';

type Props = {
  closeModal: () => void;
  onConfirm: () => void;
  onCancel: () => void;
  confirmText: React.ReactNode;
  cancelText: React.ReactNode;
  /**
   * Standard structure (docs/design-system.md → "Confirmation Dialogs"):
   * a concise `title` plus a supporting `message` underneath, with an
   * optional status `icon` top-aligned with the title.
   */
  title: React.ReactNode;
  message?: React.ReactNode;
  /**
   * Optional block rendered under the message — e.g. a list of the things the
   * confirmed action will affect (PLAT-11118). Its own slot because `message`
   * renders inside a `<p>`, which cannot legally hold a list.
   */
  details?: React.ReactNode;
  icon?: React.ReactNode;
  accentColor?: string;
};

export const ConfirmModal: React.FC<Props> = ({
  closeModal,
  onConfirm,
  onCancel,
  confirmText,
  cancelText,
  title,
  message,
  details,
  icon,
  accentColor = 'var(--destructive)',
}: Props) => {
  return (
    <Modal isOpen closeModal={closeModal}>
      <ModalWrapper data-testid="confirm-modal">
        <ModalHeading accentColor={accentColor}>
          {icon && <ModalHeadingIcon>{icon}</ModalHeadingIcon>}
          <DialogHeader>
            <ModalTitle>{title}</ModalTitle>
            {message && <DialogText>{message}</DialogText>}
          </DialogHeader>
        </ModalHeading>
        {details && <ModalDetails>{details}</ModalDetails>}
        <ModalButtonsContainer>
          <ModalCancelButton
            accentColor={accentColor}
            onClick={onCancel}
            data-testid="confirm-modal-cancel"
          >
            {cancelText}
          </ModalCancelButton>
          <ModalConfirmButton
            accentColor={accentColor}
            onClick={onConfirm}
            data-testid="confirm-modal-ok"
          >
            {confirmText}
          </ModalConfirmButton>
        </ModalButtonsContainer>
      </ModalWrapper>
    </Modal>
  );
};

// Compact confirmation-dialog standard (docs/design-system.md → "Confirmation
// Dialogs"): width pinned to the shared token so long titles/body copy never
// stretch the box, with the dialog scaffold's padding scale.
export const ModalWrapper = styled.div`
  min-width: var(--dialog-width-confirm);
  max-width: var(--dialog-width-confirm);
  border-radius: var(--radius-card);
  border: 1px solid var(--border);
  padding: var(--spacing-xl) var(--spacing-lg) var(--spacing-lg);
  box-sizing: border-box;
  background: var(--surface);
  box-shadow: var(--elevation-lg);
`;

// Left-aligned Title Case heading per the design system — no centered /
// uppercase / letter-spaced / green text. `accentColor` is still accepted for
// backward compatibility but no longer tints the title; the title is neutral
// and the destructive/affirmative intent is carried by the confirm button.
// Icons top-align with the title's first line, not the whole text block.
export const ModalHeading = styled.div<{ accentColor?: string }>`
  font-family: var(--font-primary);
  font-size: var(--text-md);
  font-weight: var(--font-weight-semi-bold);
  color: var(--foreground);
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-sm);
`;

export const ModalTitle = styled(DialogTitle)`
  line-height: var(--leading-text-md);
`;

// Centers the icon on the title's first text line.
export const ModalHeadingIcon = styled.div`
  display: flex;
  align-items: center;
  height: var(--leading-text-md);
  flex-shrink: 0;
`;

// Optional body block under the message (see the `details` prop). Sits between
// the header group and the actions row, and is a block-level sibling of the
// message rather than a child of it.
const ModalDetails = styled.div`
  margin-top: var(--spacing-md);
`;

export const ModalButtonsContainer = styled.div`
  margin-top: var(--spacing-lg);
  display: flex;
  justify-content: flex-end;
  height: var(--btn-h-lg);
  gap: var(--spacing-sm);
`;

// Neutral secondary button — Cancel is never tinted with the accent color.
export const ModalCancelButton = styled.button<{ accentColor?: string }>`
  box-sizing: border-box;
  display: inline-flex;
  height: var(--btn-h-lg);
  padding: 0 var(--btn-px-lg);
  justify-content: center;
  align-items: center;
  gap: var(--spacing-sm);
  border-radius: var(--radius-button);
  border: 1px solid var(--border);
  background: transparent;
  cursor: pointer;

  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  color: var(--foreground);
  transition:
    background 120ms ease,
    border-color 120ms ease,
    filter 120ms ease;

  &:hover:not(:disabled) {
    background: var(--surface-elevated);
    border-color: var(--text-muted);
  }

  :disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

// Solid action button — tinted by `accentColor` (destructive by default,
// `var(--primary)` for affirmative confirms). On hover the accent fill must
// stay (never fall back to the secondary button's dark hover): primary goes
// to `--primary-hover`, everything else keeps its fill and brightens.
export const ModalConfirmButton = styled(ModalCancelButton)`
  background: ${props => props.accentColor ?? 'var(--destructive)'};
  border-color: transparent;
  color: var(--background);
  font-weight: var(--font-weight-semi-bold);

  &:hover:not(:disabled) {
    /* Keep the accent fill on hover — the base button's hover swaps in
       var(--surface-elevated), which would leave var(--background) text
       unreadable on the neutral surface. Re-assert the accent + brighten. */
    background: ${props =>
      props.accentColor === 'var(--primary)'
        ? 'var(--primary-hover)'
        : (props.accentColor ?? 'var(--destructive)')};
    border-color: transparent;
    filter: ${props =>
      props.accentColor === 'var(--primary)' ? 'none' : 'brightness(1.08)'};
  }
`;
